'use client';

import { Bar, BarChart, Cell, LabelList, ResponsiveContainer, XAxis, YAxis } from 'recharts';
import { useAppSettings } from '@/hooks/useAppSettings';
import { formatMoney } from '@/lib/money';

type StatsCategory = {
  name: string;
  amount: number;
  icon?: string;
  color?: string;
};

export default function StatsCategoryChart({
  categories,
  title,
}: {
  categories: StatsCategory[];
  title: string;
}) {
  const { settings } = useAppSettings();
  const money = (value: number) => formatMoney(value, settings.currency, 0);
  const chartData = categories.map((category) => ({
    ...category,
    color: category.color ?? '#f43f5e',
    formattedAmount: money(category.amount),
  }));
  const chartHeight = Math.max(220, chartData.length * 56);

  return (
    <div className="chart-card space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-slate-100">{title}</h2>
      </div>

      {chartData.length === 0 ? (
        <div className="card-muted text-sm">Aucune dépense par catégorie pour l'instant.</div>
      ) : (
        <>
        <div className="no-print pointer-events-none rounded-2xl bg-slate-950/70 px-2 py-3 select-none" style={{ height: chartHeight }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chartData} layout="vertical" margin={{ top: 12, right: 72, bottom: 12, left: 10 }}>
              <XAxis type="number" hide />
              <YAxis
                type="category"
                dataKey="name"
                width={136}
                axisLine={false}
                tickLine={false}
                interval={0}
                tick={{ fill: '#cbd5e1', fontSize: 13, fontWeight: 700 }}
              />
              <Bar dataKey="amount" radius={[0, 12, 12, 0]} barSize={22} isAnimationActive={false}>
                {chartData.map((category) => (
                  <Cell key={category.name} fill={category.color} />
                ))}
                <LabelList dataKey="formattedAmount" position="right" fill="#f8fafc" fontSize={12} fontWeight={700} />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
        <div className="print-only pointer-events-none rounded-2xl bg-slate-950/70 px-2 py-3 select-none" style={{ height: chartHeight }}>
          <BarChart width={720} height={chartHeight} data={chartData} layout="vertical" margin={{ top: 12, right: 72, bottom: 12, left: 10 }}>
            <XAxis type="number" hide />
            <YAxis
              type="category"
              dataKey="name"
              width={136}
              axisLine={false}
              tickLine={false}
              interval={0}
              tick={{ fill: '#0f172a', fontSize: 13, fontWeight: 700 }}
            />
            <Bar dataKey="amount" radius={[0, 12, 12, 0]} barSize={22} isAnimationActive={false}>
              {chartData.map((category) => (
                <Cell key={category.name} fill={category.color} />
              ))}
              <LabelList dataKey="formattedAmount" position="right" fill="#0f172a" fontSize={12} fontWeight={700} />
            </Bar>
          </BarChart>
        </div>
        </>
      )}
    </div>
  );
}
